import styled, { keyframes } from "styled-components/macro";
import type { AxiosError } from "axios";
import type { Assets } from "../../../../../../types/types.PIM";
import { DelayedSpinner } from "../../../../../../components/DelayedSpinner/DelayedSpinner";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { get_content_type, GetDocumentIcon } from "../util/AssetsUtil";
import { useEffect, useState } from "react";

const ASSET_ERROR_RETRY_COUNT = 5;
const ASSET_ERROR_RETRY_INTERVAL = 3000;

const Container = styled.div<{ is_preview: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: ${({ is_preview }) => (is_preview ? "100%" : "234px")};
  cursor: pointer;
  > img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  iframe {
    height: 100%;
    width: 100%;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  inset: 0;
  z-index: 1;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.primaryBG};
`;

const ErrorThumbnail = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ErrorMessage = styled.div`
  position: relative;
  z-index: 1;
  padding: ${({ theme }) => theme.resolve("spacing_sm")}
    ${({ theme }) => theme.resolve("spacing_md")};
  background-color: ${({ theme }) => theme.primaryBG};
  border-radius: 4px;
`;

const retryPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const retryEllipsis = keyframes`
  0% { content: ""; }
  25% { content: "."; }
  50% { content: ".."; }
  75% { content: "..."; }
`;

const RetryingMessage = styled.span`
  animation: ${retryPulse} 1.2s ease-in-out infinite;

  &::after {
    display: inline-block;
    width: 1.25em;
    text-align: left;
    content: "";
    animation: ${retryEllipsis} 1.2s steps(1, end) infinite;
  }
`;

const AssetLoadError = ({
  is_retrying,
  thumbnail_url,
  name,
}: {
  is_retrying: boolean;
  thumbnail_url?: string | null;
  name?: string;
}) => {
  const { t } = useTranslation();
  return (
    <ErrorContainer aria-live="polite">
      {thumbnail_url && <ErrorThumbnail src={thumbnail_url} alt={name ?? ""} />}
      <ErrorMessage>
        {is_retrying ? (
          <RetryingMessage>
            {t("Failed to load the asset. Retrying")}
          </RetryingMessage>
        ) : (
          t("Failed to load the asset")
        )}
      </ErrorMessage>
    </ErrorContainer>
  );
};

const VideoUnavailableContainer = styled.div<{ mode: "thumbnail" | "preview" }>`
  display: flex;
  flex-direction: column;
  place-items: center;
  place-content: center;
  height: 100%;
  > span {
    text-align: center;
    font-size: ${({ mode }) => (mode === "thumbnail" ? "1rem" : "1.5rem")};
  }
`;

type AssetPreviewUrlSchema = {
  url: string;
  thumbnail_url?: string;
};

export const AssetDocViewer = ({
  asset,
  mode,
}: {
  asset: Assets;
  mode: "thumbnail" | "preview";
}) => {
  const { asset_type, id } = asset;
  const { t } = useTranslation();
  const [is_supported_video_format, set_is_supported_video_format] =
    useState(true);
  const [updated_uri, set_updated_uri] = useState(asset.signed_url);
  const [thumbnail_url, set_thumbnail_url] = useState<string | null>(null);
  const [is_retrying, set_is_retrying] = useState(false);
  const preview_swr_key =
    asset.asset_type === "document"
      ? asset.preview_url?.replace("/api", "") ?? null
      : asset.signed_url?.includes("https")
      ? null
      : asset.preview_url?.replace("/api", "") ?? null;

  const { data, error, isValidating } = useSWR<AssetPreviewUrlSchema>(
    preview_swr_key,
    {
      refreshInterval: 60_000,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      errorRetryCount: ASSET_ERROR_RETRY_COUNT,
      errorRetryInterval: ASSET_ERROR_RETRY_INTERVAL,
      shouldRetryOnError: true,
      onSuccess: () => set_is_retrying(false),
      onErrorRetry(err, _key, config, revalidate, { retryCount }) {
        const axiosError = err as AxiosError;
        if (axiosError?.response?.status === 404) {
          set_is_retrying(false);
          return;
        }
        const maxRetries = config.errorRetryCount ?? ASSET_ERROR_RETRY_COUNT;
        if ((retryCount ?? 0) >= maxRetries) {
          set_is_retrying(false);
          return;
        }
        set_is_retrying(true);
        setTimeout(() => {
          revalidate?.({ retryCount: (retryCount ?? 0) + 1 });
        }, config.errorRetryInterval ?? ASSET_ERROR_RETRY_INTERVAL);
      },
    }
  );

  useEffect(() => {
    set_is_retrying(false);
  }, [preview_swr_key]);

  useEffect(() => {
    set_updated_uri(data?.url || asset.signed_url);
    set_thumbnail_url(data?.thumbnail_url || null);
    set_is_supported_video_format(true);
  }, [asset.signed_url, data?.thumbnail_url, data?.url]);

  const use_thumbnail_url = thumbnail_url !== null && mode === "thumbnail";

  const is_loading =
    !data &&
    !error &&
    !asset.signed_url?.includes("https") &&
    !!asset.preview_url;

  if (error) {
    return (
      <Container is_preview={mode === "preview"}>
        <AssetLoadError
          is_retrying={is_retrying || isValidating}
          thumbnail_url={thumbnail_url}
          name={asset.name}
        />
      </Container>
    );
  }

  return is_loading ? (
    <DelayedSpinner />
  ) : (
    <Container is_preview={mode === "preview"} id={`doc-preview-${id}`}>
      {asset_type === "document" && (
        <>
          {use_thumbnail_url ? (
            <img
              id={`asset-doc-img-${id}`}
              src={thumbnail_url}
              alt={asset.name}
              title={asset.name}
            />
          ) : (
            <iframe
              src={updated_uri}
              id={`asset-doc-iframe-${id}`}
              title={asset.name}
              loading="lazy"
            />
          )}
        </>
      )}
      {asset_type === "image" && (
        <img
          id={`asset-img-preview-${id}`}
          src={updated_uri}
          alt={asset.name}
          title={asset.name}
        />
      )}
      {asset_type === "video" && (
        <>
          {is_supported_video_format ? (
            <video
              id={`asset-vid-preview-${id}`}
              src={updated_uri}
              controls
              onError={() => set_is_supported_video_format(false)}
            />
          ) : (
            <VideoUnavailableContainer mode={mode}>
              <GetDocumentIcon
                asset_type={asset.asset_type}
                file_type={get_content_type(
                  asset.content_type ?? "application/pdf"
                )}
                width={mode === "thumbnail" ? 34 : 50}
                height={mode === "thumbnail" ? 34 : 50}
              />
              <span>
                {t(
                  "Your browser does not support the {{video_format}} video format.",
                  {
                    video_format: get_content_type(
                      asset.content_type ?? "application/pdf"
                    ),
                  }
                )}
              </span>
            </VideoUnavailableContainer>
          )}
        </>
      )}
    </Container>
  );
};
